import type { CustomerOrderDetail } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { ORDER_PROGRESS_STEPS, orderProgressOf } from '@/entities/order';
import { formatCalendarDate, formatDate } from '@/shared/lib';
import { cn } from '@/shared/ui';

interface OrderProgressStepsProps {
  readonly order: CustomerOrderDetail;
}

/** 단계마다 그 단계에 들어선 날 — 주문(결제 대기)·결제·제작 착수·출고는 시각, 배송 완료는 달력 날짜다. */
const stepDates = (order: CustomerOrderDetail): readonly (string | null)[] => [
  formatDate(order.createdAt),
  order.paidAt === null ? null : formatDate(order.paidAt),
  order.productionStartedAt === null ? null : formatDate(order.productionStartedAt),
  order.shippedAt === null ? null : formatDate(order.shippedAt),
  order.deliveredOn === null ? null : formatCalendarDate(order.deliveredOn),
];

/**
 * 진행 단계 — 내 견적 진행 단계와 같은 굵은 선(지나온 단계 흑연, 지금 단계 라임, 남은 단계 옅은 선)에 단계에 들어선 날을 더한다.
 * 결제 전 취소·환불이면 멈춘 단계에서 끝났다고 적는다.
 */
export const OrderProgressSteps = ({ order }: OrderProgressStepsProps): ReactElement => {
  const { currentIndex, ended } = orderProgressOf(order);
  const dates = stepDates(order);

  return (
    <section aria-label="주문 진행" className="flex flex-col gap-3">
      <ol className="grid grid-cols-5 gap-1.5 sm:gap-2">
        {ORDER_PROGRESS_STEPS.map((step, index) => {
          const isDone = index < currentIndex;
          const isCurrent = index === currentIndex;
          const date = dates[index] ?? null;

          return (
            <li
              key={step}
              aria-current={isCurrent ? 'step' : undefined}
              className="flex flex-col gap-3"
            >
              <span
                aria-hidden
                className={cn(
                  'block h-1',
                  isDone && (ended ? 'bg-foreground/40' : 'bg-foreground'),
                  isCurrent && (ended ? 'bg-foreground/40' : 'bg-brand-lime'),
                  !isDone && !isCurrent && 'bg-foreground/10',
                )}
              />
              <span className="flex flex-col gap-1">
                <span className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-2">
                  <span aria-hidden className="text-[11px] text-muted-foreground tabular-nums">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span
                    className={cn(
                      'text-[13px] sm:text-sm',
                      isCurrent ? 'font-bold text-foreground' : undefined,
                      isDone ? 'text-foreground' : undefined,
                      !isDone && !isCurrent ? 'text-muted-foreground' : undefined,
                    )}
                  >
                    {step}
                  </span>
                </span>
                {date !== null ? (
                  <span className="text-[11px] text-muted-foreground tabular-nums">{date}</span>
                ) : null}
              </span>
            </li>
          );
        })}
      </ol>
      {ended ? (
        <p className="text-xs text-muted-foreground">이 주문은 이 단계에서 진행이 끝났습니다.</p>
      ) : null}
    </section>
  );
};
